/**
 * Central sports configuration
 * Single source of truth for all sport-related constants and metadata
 */

import sportsSnapshot from './sports-snapshot.json'
import sportsData from './sports.json'

export enum SPORT_ID {
  UNDEFINED = 0,
  CRICKET = 1,
  SOCCER = 2,
  VOLLEYBALL = 3,
  BASEBALL = 6,
  NETBALL = 7,
  FIELD_HOCKEY = 8,
  ICE_HOCKEY = 9,
  HANDBALL = 10,
  TENNIS = 11,
  CRICKET_MANUAL = 12,
  BADMINTON = 13,
  BASKETBALL = 14,
  BEACH_VOLLEYBALL = 15,
  FUTSAL = 16,
  INDOOR_CRICKET = 17,
  PADEL = 18,
  PICKLEBALL = 19,
  RUGBY_LEAGUE = 20,
  RUGBY_UNION = 21,
  SOFTBALL = 22,
  SQUASH = 23,
  TABLE_TENNIS = 24,
  AFL = 25,
  WATER_POLO = 26,
}

const DISCIPLINARY_CARDS = ['green', 'yellow', 'red'] as const
export type DisciplinaryCard = (typeof DISCIPLINARY_CARDS)[number]

const SCORING_FAMILIES = ['goal', 'baseball', 'standard', 'cricketManual', 'tennis'] as const
export type ScoringFamily = (typeof SCORING_FAMILIES)[number]

export interface SportConfig {
  id: SPORT_ID
  name: string
  folder: string
  defaultDemoPayload: string
  urlAliases: string[]
  // Scoring config from the gfx API's GET /sports (FBX-2281): which template
  // family renders the sport, and for goal sports the period count and card
  // colours. Seeded from sports-snapshot.json, then replaced by a live fetch.
  scoringFamily?: ScoringFamily
  periodCount?: number
  disciplinary?: DisciplinaryCard[]
  // GoalSport-style render labels (soccer, netball, hockey, etc.). Optional —
  // only set on sports that use the GoalSport template.
  periodLabels?: string[]
  breakLabels?: string[]
  endLabel?: string
  scoreNoun?: string
}

interface SportConfigJson {
  id: number
  name: string
  folder: string
  defaultDemoPayload: string
  urlAliases: string[]
  periodLabels?: string[]
  breakLabels?: string[]
  endLabel?: string
  scoreNoun?: string
}

/**
 * Sport configurations indexed by SPORT_ID
 * Render fields load from sports.json (shared with server.js); scoring fields
 * are applied from sports-snapshot.json below, then from the live API.
 */
export const SPORTS: Record<number, SportConfig> = Object.fromEntries(
  Object.entries(sportsData.sports as Record<string, SportConfigJson>).map(([key, value]) => [
    Number(key),
    {
      id: value.id as SPORT_ID,
      name: value.name,
      folder: value.folder,
      defaultDemoPayload: value.defaultDemoPayload,
      urlAliases: value.urlAliases,
      periodLabels: value.periodLabels,
      breakLabels: value.breakLabels,
      endLabel: value.endLabel,
      scoreNoun: value.scoreNoun,
    },
  ])
)

/**
 * Overlay the scoring config the gfx API serves at GET /sports (FBX-2281),
 * the same source Go and Go Lite score from, onto the render config.
 *
 * The API owns scoringFamily, periodCount and disciplinary. Render-only
 * fields (folder, demo payloads, URL aliases, period and break labels) stay
 * local, and so does name: it is only the demo picker label here, and the
 * API names both cricket ids "Cricket". A sport the bundle does not know
 * yet is added so its family template can render it.
 *
 * Entries that fail validation are skipped, leaving the previous values.
 * Returns how many sports were applied.
 */
export const applyRemoteSports = (raw: unknown): number => {
  const sports = (raw as { sports?: unknown } | null)?.sports
  if (!sports || typeof sports !== 'object') return 0

  let applied = 0
  for (const entry of Object.values(sports as Record<string, unknown>)) {
    const remote = parseRemoteSport(entry)
    if (!remote) continue
    const local = SPORTS[remote.id]
    SPORTS[remote.id] = {
      ...(local ?? { id: remote.id, name: remote.name, folder: '', defaultDemoPayload: '', urlAliases: [] }),
      scoringFamily: remote.scoringFamily,
      periodCount: remote.periodCount,
      disciplinary: remote.disciplinary,
    }
    applied++
  }
  return applied
}

interface RemoteSport {
  id: number
  name: string
  scoringFamily: ScoringFamily
  periodCount?: number
  disciplinary?: DisciplinaryCard[]
}

const parseRemoteSport = (entry: unknown): RemoteSport | null => {
  if (!entry || typeof entry !== 'object') return null
  const { id, name, scoringFamily, periodCount, disciplinary } = entry as Record<string, unknown>
  if (!Number.isInteger(id) || typeof name !== 'string' || typeof scoringFamily !== 'string') return null
  if (!(SCORING_FAMILIES as readonly string[]).includes(scoringFamily)) return null
  if (periodCount !== undefined && !(Number.isInteger(periodCount) && (periodCount as number) > 0)) return null
  if (
    disciplinary !== undefined &&
    !(Array.isArray(disciplinary) && disciplinary.every((c) => (DISCIPLINARY_CARDS as readonly string[]).includes(c)))
  ) {
    return null
  }
  return {
    id: id as number,
    name,
    scoringFamily: scoringFamily as ScoringFamily,
    periodCount: periodCount as number | undefined,
    disciplinary: disciplinary as DisciplinaryCard[] | undefined,
  }
}

// Offline floor: the last /sports response saved by `yarn sports:snapshot`.
// loadSportConfig() replaces it with a live copy at page load.
applyRemoteSports(sportsSnapshot)

/**
 * Sports that have demo mode support (in display order)
 */
export const DEMO_SPORTS: SPORT_ID[] = sportsData.demoSports as SPORT_ID[]

/**
 * Get sport config by URL parameter value
 * Supports aliases (e.g., 'soccer' maps to FOOTBALL)
 */
export const getSportByUrlParam = (param: string): SPORT_ID | undefined => {
  const normalised = param.toLowerCase()
  for (const sport of Object.values(SPORTS)) {
    if (sport.urlAliases.includes(normalised)) {
      return sport.id
    }
  }
  return undefined
}

/**
 * Get sport config by ID
 */
export const getSportConfig = (sportId: SPORT_ID): SportConfig | undefined => {
  return SPORTS[sportId]
}

/**
 * Get default demo payload path for a sport
 */
export const getDefaultDemoPayload = (sportId: SPORT_ID): string | undefined => {
  return SPORTS[sportId]?.defaultDemoPayload
}
